import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Timestamp } from "firebase/firestore";
import { GetStaticProps } from "next";
import Head from "next/head";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";

import BrowseChefsTab from "../components/browse/BrowseChefsTab";
import BrowseGigsTab from "../components/browse/BrowseGigsTab";
import Filter from "../components/browse/Filter";
import TopBar from "../components/browse/TopBar";
import InnerLayout from "../components/innerLayout/InnerLayout";
import BrowseType from "../constants/BrowseType";
import {
  CHEF_PAGINATION_LIMIT,
  GIG_PAGINATION_LIMIT,
} from "../constants/General";
import TabsConstants from "../constants/tabs";
import UserType from "../constants/UserType";
import { getGigs } from "../firebase/gig";
import { getChefs } from "../firebase/user";
import { Gig } from "../interfaces/Gig";
import User from "../interfaces/User";
import { setActiveTab } from "../redux/createBrowsePageSlice";
import { setTab } from "../redux/createTabSlice";
import { RootState } from "../redux/store";
import { Island } from "../utils/get-zip-code";

interface BrowseProps {
  initialGigsData: Gig[];
  initialChefsData: User[];
  lastUpdated: string;
}

const Browse = ({
  initialGigsData,
  initialChefsData,
  lastUpdated,
}: BrowseProps) => {
  const activeBrowseTab = useSelector(
    (state: RootState) => state.browsePage.activeTab
  );
  const userUid = useSelector((state: RootState) => state.user.user.uid);
  const userType = useSelector((state: RootState) => state.user.user.userType);
  const [isFilterPageOpen, setIsFilterPageOpen] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [gigsData, setGigsData] = useState<Gig[]>(initialGigsData);
  const [chefData, setChefData] = useState<User[]>(initialChefsData);
  const shouldRun = useRef(false);
  const dispatch = useDispatch();
  const [docId, setDocId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const { ref, inView } = useInView();
  const [loading, setIsLoading] = useState(false);
  const [isDisplayingFavourites, setIsDisplayingFavourites] = useState(false);
  const [triggerPaginate, setTriggerPaginate] = useState(true);
  const [locationFilter, setLocationFilter] = useState<Island>();

  useEffect(() => {
    dispatch(setTab(TabsConstants.Search));
    if (!activeBrowseTab) {
      if (userType === UserType.Client) {
        dispatch(setActiveTab(BrowseType.Chef));
      } else if (userType === UserType.Chef) {
        dispatch(setActiveTab(BrowseType.Gig));
      } else {
        dispatch(setActiveTab(BrowseType.Chef));
      }
    }
  }, []);

  useEffect(() => {
    if (shouldRun.current) {
      setIsLoading(true);
      setDocId(null);
      setPage(0);
      setGigsData([]);
      setChefData([]);
      switch (activeBrowseTab) {
        case BrowseType.Gig:
          fetchGigs(0, null);
          break;

        case BrowseType.Chef:
          fetchChefs(0, null);
          break;
      }
    }
    shouldRun.current = true;
  }, [activeBrowseTab, isDisplayingFavourites, locationFilter]);

  useEffect(() => {
    setIsLoading(true);
    switch (activeBrowseTab) {
      case BrowseType.Gig:
        fetchGigs(page, docId);
        break;

      case BrowseType.Chef:
        fetchChefs(page, docId);
        break;
    }
  }, [triggerPaginate]);

  async function fetchGigs(page: number, docId: string | null) {
    const response: Gig[] =
      (await getGigs(
        {
          limit: GIG_PAGINATION_LIMIT,
          page,
        },
        docId,
        isDisplayingFavourites && userUid ? userUid : null,
        userUid || null
      )) || [];

    setIsLoading(false);
    if (response !== undefined) {
      setGigsData((prevData) => {
        return prevData.concat(response);
      });
      setHasMoreData(response.length !== 0);
    }
  }

  async function fetchChefs(page: number, docId: string | null) {
    const response: User[] =
      (await getChefs(
        {
          limit: CHEF_PAGINATION_LIMIT,
          page,
        },
        docId,
        isDisplayingFavourites && userUid ? userUid : null,
        locationFilter
      )) || [];
    setIsLoading(false);
    if (response !== undefined) {
      setChefData((prevData) => {
        return prevData.concat(response);
      });
      setHasMoreData(response.length !== 0);
    }
  }

  // Infinite Scroll
  useEffect(() => {
    if (inView && hasMoreData && !loading) {
      switch (activeBrowseTab) {
        case BrowseType.Gig:
          if (gigsData.length > 0) {
            setDocId(gigsData[gigsData.length - 1].gigId || null);
          }
          break;

        case BrowseType.Chef:
          if (chefData.length > 0) {
            setDocId(chefData[chefData.length - 1].uid || null);
          }
          break;
      }
      setPage((prevPage) => prevPage + 1);
      setTriggerPaginate((latestState) => !latestState);
    }
  }, [inView]);

  const displayBrowseTabs = () => {
    if (activeBrowseTab === BrowseType.Gig) {
      return <BrowseGigsTab gigs={gigsData} isLoading={loading} />;
    } else if (activeBrowseTab === BrowseType.Chef) {
      return <BrowseChefsTab chefs={chefData} isLoading={loading} />;
    }
    return null;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Head>
        <title>
          More Pleaze: Private chef experiences where gourmet meets health
        </title>

        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <meta
          property="og:title"
          content="More Pleaze: Private chef experiences where gourmet meets health"
          key="title"
        />

        <meta
          name="description"
          content="More Pleaze: Private chef experiences where gourmet meets wellness, approachable meets luxury, and all are welcome. Book your chef today!"
        />

        <meta
          name="keywords"
          content="Private Chef Services, Personal Chef at Home, In-Home Dining Experience, Custom Menu Creation, Gourmet Meal Preparation, Exclusive Dinner Parties, Luxury Catering, Bespoke Culinary Services, Fine Dining in Your Home, Chef for Special Occasions, Personalized Cooking Classes, Farm-to-Table Chef, Healthy Meal Planning, Seasonal and Local Cuisine, Dietary-Specific Chef (e.g., Vegan, Gluten-Free), Intimate Event Catering, Culinary Experience Designer, Private Brunch Services, Corporate Event Chef, Artisanal Ingredients
"
        />

        <meta name="author" content="morepleaze.com" />

        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="More Pleaze: Private chef experiences where gourmet meets health"
        />
        <meta
          property="og:description"
          content="More Pleaze: Private chef experiences where gourmet meets wellness, approachable meets luxury, and all are welcome. Book your chef today!"
        />
        <meta property="og:url" content="https://morepleaze.com" />
      </Head>

      <Box sx={{ width: "100%" }}>
        <TopBar
          setIsDisplayingFavourites={setIsDisplayingFavourites}
          browseType={activeBrowseTab}
        />
        <Box sx={{ py: { xs: "82px", md: "0px" } }}>
          {/* {activeBrowseTab === BrowseType.Chef && (
            <Filter value={locationFilter} handleChange={setLocationFilter} />
          )} */}
          {displayBrowseTabs()}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "10px 0",
            }}
            ref={ref}
          >
            {loading ? (
              <Box
                sx={{
                  display: "grid",
                  placeItems: "center",
                  height: "100%",
                  width: "100%",
                  background: "transparent",
                }}
              >
                <CircularProgress color="primary" />
              </Box>
            ) : null}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

// Utility to recursively convert Firestore Timestamps to ISO strings and undefined to null
function sanitizeFirestoreData(obj: any): any {
  if (obj instanceof Timestamp) {
    return obj.toDate().toISOString();
  } else if (Array.isArray(obj)) {
    return obj.map(sanitizeFirestoreData);
  } else if (obj && typeof obj === "object") {
    const newObj: any = {};
    for (const key in obj) {
      const value = sanitizeFirestoreData(obj[key]);
      newObj[key] = value === undefined ? null : value;
    }
    return newObj;
  }
  return obj;
}

export const getStaticProps: GetStaticProps<BrowseProps> = async () => {
  try {
    // Fetch initial data for both gigs and chefs
    const [gigsData, chefsData] = await Promise.all([
      getGigs(
        {
          limit: GIG_PAGINATION_LIMIT,
          page: 1,
        },
        null,
        null,
        null
      ),
      getChefs(
        {
          limit: CHEF_PAGINATION_LIMIT,
          page: 1,
        },
        null,
        null
      ),
    ]);

    // Sanitize data for Next.js serialization
    const sanitizedGigsData = sanitizeFirestoreData(gigsData || []);
    const sanitizedChefsData = sanitizeFirestoreData(chefsData || []);

    return {
      props: {
        initialGigsData: sanitizedGigsData,
        initialChefsData: sanitizedChefsData,
        lastUpdated: new Date().toISOString(),
      },
      // Revalidate every hour (3600 seconds)
      revalidate: 3600,
    };
  } catch (error) {
    console.error("Error in getStaticProps:", error);
    return {
      props: {
        initialGigsData: [],
        initialChefsData: [],
        lastUpdated: new Date().toISOString(),
      },
      revalidate: 3600,
    };
  }
};

Browse.getLayout = function getLayout(component: ReactElement) {
  return <InnerLayout>{component}</InnerLayout>;
};

export default Browse;
